import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import {
  EVENT_QUERY_STRINGS,
  EVENT_TEMPLATE_ID_TYPE_QUERY_VALUE_MAP,
  EVENT_TIMELINE_QUERY_VALUE_MAP,
} from '../../constants/eventsFairs.constants';
import { getUTCDate } from '../../hooks/useFormatDate';
import { updateSearchResults } from '../../hooks/useQueryParams.effect';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { addToAnyScript } from '../../utils/addToAnyScript.utils';
import { canShowEventCard, getCanUseDate, getEndOfDate, getTimeline } from '../../utils/eventsListings.utils';
import NoMatch from '../@shared/filters/components/noMatch';
import Pagination from '../@shared/pagination';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import DesktopFilter from './components/desktopFilter';
import EventCardList from './components/eventCardList';
import MobileFilter from './components/mobileFilter';
import { EventsListingModel } from './models/eventsListing.model';
import { EventListingFilters, TEventsListingProps, TSelectedEventsFilters } from './types/eventsListing.types';

const EventsListing: FC<TEventsListingProps> = (props) => {
  const model = new EventsListingModel(props);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx['lg']})`);
  const location = useLocation();
  const filterQueryString = location?.search;
  const filterParams = useMemo(() => new URLSearchParams(location?.search), [filterQueryString]);
  const canShowEventTimeLine = model.canShowFilter(EventListingFilters.TIMELINE);
  const canShowEventType = model.canShowFilter(EventListingFilters.TYPE);
  const selectedTimeline = canShowEventTimeLine ? filterParams.get(EVENT_QUERY_STRINGS.EVENT_TIMELINE) || '' : '';
  const selectedType = canShowEventType ? filterParams.get(EVENT_QUERY_STRINGS.EVENT_TYPE) || '' : '';
  const selectedPage = filterParams.get(EVENT_QUERY_STRINGS.EVENT_PAGE) || '1';
  const selectedPageNo = parseInt(selectedPage);
  const noOfCardsPerPage = 6;
  const startIndex = (selectedPageNo - 1) * noOfCardsPerPage;
  const endIndex = selectedPageNo * noOfCardsPerPage;

  const { EVENT_TYPE, EVENT_TIMELINE } = EVENT_QUERY_STRINGS;
  const selectedFilters: TSelectedEventsFilters = {
    [EVENT_TIMELINE]: selectedTimeline,
    [EVENT_TYPE]: selectedType,
  };

  const isTypeFilterPresent: { [key in 'event' | 'live-event' | 'fair']: boolean } = { event: false, fair: false, 'live-event': false };
  const isTimeLineFilterPresent: { [key in keyof typeof EVENT_TIMELINE_QUERY_VALUE_MAP]: boolean } = {
    ONGOING: false,
    UPCOMING: false,
    PAST: false,
  };
  const typeFilterList: [{ id: string; title: string }] = [{ id: 'type', title: t('cnhi-events-all-types') }];
  const timelineFilterList: [{ id: string; title: string }] = [{ id: 'timeline', title: t('cnhi-events-all-timelines') }];

  const filteredEvents = useMemo(
    () =>
      model.items?.filter?.((eachItem) => {
        const { templateId, startDate, endDate } = eachItem;
        const canUseStartDate = getCanUseDate(startDate);
        const canUseEndDate = getCanUseDate(endDate);
        const eventTimeline =
          startDate && canUseStartDate
            ? getTimeline(getUTCDate(startDate), endDate && canUseEndDate ? getUTCDate(endDate) : getEndOfDate(startDate))
            : '';

        if (!canShowEventCard(eachItem)) return false;

        let isTypeMatching = false;
        let isTimelineMatching = false;
        eventTimeline && (eachItem.eventTimeline = eventTimeline);

        let isCardMatchingFilters = false;

        if (!selectedType && !selectedTimeline) {
          isCardMatchingFilters = true;
        } else {
          if (!selectedType) isTypeMatching = true;
          else if (!templateId) isTypeMatching = false;
          else isTypeMatching = EVENT_TEMPLATE_ID_TYPE_QUERY_VALUE_MAP[templateId] === selectedType;

          if (!selectedTimeline) isTimelineMatching = true;
          else if (!startDate) isTimelineMatching = false;
          else {
            isTimelineMatching = EVENT_TIMELINE_QUERY_VALUE_MAP[eventTimeline] === selectedTimeline;
          }
          isCardMatchingFilters = isTypeMatching && isTimelineMatching;
        }

        if (isCardMatchingFilters) {
          templateId && (isTypeFilterPresent[EVENT_TEMPLATE_ID_TYPE_QUERY_VALUE_MAP[templateId]] = true);
          isTimeLineFilterPresent[eventTimeline] = true;
        }

        return isCardMatchingFilters;
      }),
    [selectedType, selectedTimeline, selectedPageNo, props]
  );

  const filteredEventsToShow = filteredEvents?.slice?.(startIndex, endIndex) || [];

  useMemo(() => {
    if (canShowEventType) {
    if (isTypeFilterPresent.event) typeFilterList.push({ id: 'event', title: t('cnhi-event-filter-title') });
    if (isTypeFilterPresent['live-event']) typeFilterList.push({ id: 'live-event', title: t('cnhi-live-event-filter-title') });
    if (isTypeFilterPresent.fair) typeFilterList.push({ id: 'fair', title: t('cnhi-fair-filter-title') });
    }
    if (canShowEventTimeLine) {
    if (isTimeLineFilterPresent.ONGOING || isTimeLineFilterPresent.UPCOMING)
      timelineFilterList.push({ id: 'latest', title: t('cnhi-latest-filter-title') });
    if (isTimeLineFilterPresent.PAST) timelineFilterList.push({ id: 'past', title: t('cnhi-past-filter-title') });
    }
  }, [selectedType, selectedTimeline, selectedPageNo, props]);

  const totalPageCount = Math.ceil(filteredEvents.length / noOfCardsPerPage);

  useEffect(() => {
    setTimeout(() => {
      addToAnyScript();
    });
  }, [props]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  const onPaginationChange = (selectedPage: number): void =>
    updateSearchResults(navigate, { [EVENT_QUERY_STRINGS.EVENT_PAGE]: selectedPage.toString() });

  return (
    <div className="events-listing">
       {(canShowEventTimeLine || canShowEventType) ?
        isWide ? (
        <DesktopFilter
          model={model}
          selectedFilters={selectedFilters}
          typeFilterDropdownOptions={typeFilterList}
          timelineFilterDropdownOptions={timelineFilterList}
        />
      ) : (
        <MobileFilter
          model={model}
          selectedFilters={selectedFilters}
          typeFilterDropdownOptions={typeFilterList}
          timelineFilterDropdownOptions={timelineFilterList}
        />
      )
      : null}
      {filteredEventsToShow?.length === 0 ? (
        <NoMatch
          titleDictionaryKey="cnhi-events-no-matches-found-title"
          descriptionDictionaryKey="cnhi-events-no-matches-found-description"
        />
      ) : (
        <div className="events-listing__card-container">
            <EventCardList items={filteredEventsToShow} itemData={model.itemData} rendering={props.rendering}/>
        </div>
      )}
      <Pagination currentPage={selectedPageNo} pages={totalPageCount} goToPage={onPaginationChange} />
    </div>
  );
};

export default EventsListing;
